import React, { useState } from "react";
import ReactFlow, {Controls, Node, Edge } from "reactflow";
import "reactflow/dist/style.css";

import { nodeTypes } from "./CommunityFlow/nodeTypes";
import { edgeTypes } from "components/Flow/Edges/edgeTypes";
import useUpdatedLocalNodeValues from "components/Flow/Nodes/useUpdatedLocalNodeValues";
import useUpdatedLocalEdgeValues from "components/Flow/Edges/useUpdatedLocalEdgeValues";
import AddNodeButton from "components/Flow/Controls/AddNodeButton";
import StandardBackground from "components/Flow/StandardBackground";

import { 
  getCommunityPathwayData,
  communityNodeAPI,
  communityEdgeAPI,
  communityNodeTypesAPI
} from "api/Community/CommunityAPI";

import {
  onNodesChangeHandler,
  onNodeClickHandler,
  onNodeDragStopHandler,
  onPaneClickHandler,
  reloadFlowData,
  useDeleteNodeHotkey,
} from "components/Flow/Nodes/nodeHandlers";

import {
  onConnectHandler,
  onEdgeClickHandler,
} from "components/Flow/Edges/edgeHandlers";

import ControlPanel from "components/Flow/Controls/ControlPanel";
import DeleteConfirmDialog from "components/Flow/Nodes/DeleteConfirmDialog";
import RecalculateButton from "components/Flow/Controls/RecalculateButton";


interface CommunityFlowProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onNodeSelect: (node: Node | null) => void;
  CommunityId: string;
  onNodesReload?: () => Promise<void>;
}

const CommunityFlow: React.FC<CommunityFlowProps> = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodeSelect,
  CommunityId,
}) => {

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const updatedNodes = useUpdatedLocalNodeValues(nodes, { selectedNodeId: selectedNode?.id, update: { type: "communityPathwayNode" } });
  const updatedEdges = useUpdatedLocalEdgeValues(edges, { update: { type: "standardEdge" } });
  
  const onNodesReload = () => reloadFlowData(CommunityId, setNodes, setEdges, getCommunityPathwayData);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // triggered by hotkey
  const requestDelete = React.useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = React.useCallback(async () => {
    if (!selectedNode) return;
    
    // Optimistically remove from UI
    setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
    setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));

    try {
      await communityNodeAPI.delete(selectedNode.id);
    } catch (err) {
      console.error("Failed to delete node:", err);
    }

    setSelectedNode(null);
    setDeleteDialogOpen(false);
  }, [selectedNode]);

  useDeleteNodeHotkey(selectedNode, requestDelete);

  const cancelDelete = React.useCallback(() => setDeleteDialogOpen(false), []);
  
  const updateNodeFunctionality = (
    nodeId: string,
    functionalityId: number,
    field: string,
    value: any,
    newValueId?: number
  ) => {
    setNodes(nodes =>
      nodes.map(node =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                functionalities: node.data.functionalities.map((f: any) =>
                  f.id === functionalityId
                    ? { 
                        ...f, 
                        [field]: value,
                        ...(newValueId ? { valueId: newValueId } : {}) // ⭐ THIS IS CRITICAL
                      }
                    : f
                ),
              },
            }
          : node
      )
    );
  };



  const updateNode = (
    nodeId: string,
    frontendKey: string,
    value: any
  ) => {
    setNodes(nodes =>
      nodes.map(node =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                [frontendKey]: value
              },
            }
          : node
      )
    );
  };

  

  return (
    <div style={{ height: "80vh", width: "100%", position: "relative" }}>
      <ReactFlow
        nodes={updatedNodes.map(n => ({
          ...n,
          data: {
            ...n.data,
            updateNodeFunctionality: n.data.updateNodeFunctionality ?? updateNodeFunctionality,
            updateNode: n.data.updateNode ?? updateNode,
          }
        }))}
        edges={updatedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={(changes) => onNodesChangeHandler(setNodes, changes)}
        onNodeClick={(_e, node) => {
          setSelectedNode(node);
          onNodeClickHandler(node, onNodeSelect);
        }}
        onPaneClick={() => {
          setSelectedNode(null);
          onNodeSelect(null);
          onPaneClickHandler(onNodeSelect);
        }}
        onNodeDragStop={(_e, node) => onNodeDragStopHandler(node, setNodes, communityNodeAPI.update)}
        onConnect={(connection) => onConnectHandler(connection, setEdges, nodes, communityEdgeAPI.create)}
        onEdgeClick={(_e, edge) => onEdgeClickHandler(edge, setEdges, communityEdgeAPI.delete)}
        fitView
        snapToGrid
        snapGrid={[20, 20]}
        minZoom = {0.1}
        maxZoom = {4}
        deleteKeyCode={null}
      >
        
        <Controls />
        <StandardBackground />
        <ControlPanel>
          <AddNodeButton
            setNodes={setNodes}
            setEdges={setEdges}
            systemId={CommunityId}
            onNodesReload={() => reloadFlowData(CommunityId, setNodes, setEdges, getCommunityPathwayData)}
            getAllNodeTypesFn={communityNodeTypesAPI.getAll}
            createNodeFn={communityNodeAPI.create}
            getFlowDataFn={getCommunityPathwayData}
            parentKey="community"
          />
          <RecalculateButton 
            id={CommunityId}
            reloadFlowData={onNodesReload}
          />
        </ControlPanel>
      </ReactFlow>
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        warningMessage="Are you sure you want to delete this node?"
      />
    </div>
  );
};

export default CommunityFlow;
